import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, Outlet } from 'react-router-dom';
import { LanguageProvider } from './contexts/LanguageContext';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { Toaster } from './components/ui/sonner';

// Components
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import AppBottomNav from './components/AppBottomNav';

// Landing Pages
import HomePage from './pages/HomePage';
import ProductPage from './pages/ProductPage';
import HowItWorksPage from './pages/HowItWorksPage';
import CompanyPage from './pages/CompanyPage';
import ContactPage from './pages/ContactPage';

// Auth Pages
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import AuthCallback from './pages/AuthCallback';

// App Pages
import DashboardPage from './pages/DashboardPage';
import DeviceControlPage from './pages/DeviceControlPage';
import MapPage from './pages/MapPage';
import LogsPage from './pages/LogsPage';
import AlertsPage from './pages/AlertsPage';
import AdminPage from './pages/AdminPage';
import SettingsPage from './pages/SettingsPage';
import TelemetryPage from './pages/TelemetryPage';

import './App.css';

// Protected Route Component
const ProtectedRoute = () => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-fac-dark flex items-center justify-center">
        <div className="text-fac-green font-heading text-xl">Loading...</div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return <Outlet />;
};

// Landing Layout
const LandingLayout = () => {
  return (
    <>
      <Navbar />
      <main>
        <Outlet />
      </main>
      <Footer />
    </>
  );
};

// App Layout (for logged-in users)
const AppLayout = () => {
  return (
    <>
      <Navbar />
      <main>
        <Outlet />
      </main>
      <AppBottomNav />
    </>
  );
};

// Auth Layout (minimal)
const AuthLayout = () => {
  return (
    <>
      <Navbar />
      <main>
        <Outlet />
      </main>
    </>
  );
};

function AppRoutes() {
  return (
    <Routes>
      {/* Landing Pages */}
      <Route element={<LandingLayout />}>
        <Route path="/" element={<HomePage />} />
        <Route path="/product" element={<ProductPage />} />
        <Route path="/how-it-works" element={<HowItWorksPage />} />
        <Route path="/company" element={<CompanyPage />} />
        <Route path="/contact" element={<ContactPage />} />
      </Route>

      {/* Auth Pages */}
      <Route element={<AuthLayout />}>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/register" element={<RegisterPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
      </Route>

      {/* Google OAuth Callback (no layout) */}
      <Route path="/auth/callback" element={<AuthCallback />} />

      {/* Protected App Routes */}
      <Route element={<ProtectedRoute />}>
        <Route element={<AppLayout />}>
          <Route path="/app" element={<DashboardPage />} />
          <Route path="/app/device/:id" element={<DeviceControlPage />} />
          <Route path="/app/map" element={<MapPage />} />
          <Route path="/app/telemetry" element={<TelemetryPage />} />
          <Route path="/app/logs" element={<LogsPage />} />
          <Route path="/app/alerts" element={<AlertsPage />} />
          <Route path="/app/settings" element={<SettingsPage />} />
          <Route path="/app/admin" element={<AdminPage />} />
        </Route>
      </Route>

      {/* Catch all */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <LanguageProvider>
        <AuthProvider>
          <div className="App min-h-screen bg-fac-dark">
            <AppRoutes />
            <Toaster 
              position="top-center"
              toastOptions={{
                style: {
                  background: '#1E1E1E',
                  color: '#fff',
                  border: '1px solid #333',
                },
              }}
            />
          </div>
        </AuthProvider>
      </LanguageProvider>
    </BrowserRouter>
  );
}

export default App;
